import { Link } from 'react-router-dom';
import type { WorkspaceFile } from '../lib/types';
import { getFileIcon } from '../lib/files';
import { formatDateTime } from '../lib/text';
import LinkedBadge from './LinkedBadge';

// A file row. Links to the file's detail page by default; pass `onClick` to
// make it an action instead (e.g. download a generated metadata file that has
// no detail page). Set `showFolder` to display the file's folder path beneath
// the name — useful in search results that span folders (in browse mode the
// enclosing folder is already implied by the breadcrumb). `linkQuery` appends a
// query string (e.g. "?type=...") to the file-detail link so context carries
// through (used when browsing for a specific document type to link). Pass
// `sourceId` to make the folder subtitle its own link to that folder's detail
// page (mirroring the candidate rows on the document-type page) — the filename
// then links to the file and the folder label to the folder.
export interface FileRowProps {
    file: WorkspaceFile;
    onClick?: () => void;
    showFolder?: boolean;
    linkQuery?: string;
    linked?: boolean;
    sourceId?: string;
}

export default function FileRow({ file, onClick, showFolder, linkQuery, linked, sourceId }: FileRowProps) {
    const Icon = getFileIcon(file.mime_type);
    const folderLabel = (file.source_path || '')
        .split('/')
        .filter(Boolean)
        .join(' / ');
    const fileHref = `/files/${file.id}${linkQuery ?? ''}`;
    // linkQuery starts with "?"; reuse it as "&" here.
    const folderHref = sourceId && file.source_path
        ? `/sources/${sourceId}/folder?path=${encodeURIComponent(file.source_path)}${linkQuery ? `&${linkQuery.slice(1)}` : ''}`
        : null;
    const cls = "block hover:bg-offwhite rounded-lg transition-colors";

    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={`${cls} w-full text-left`}>
                <div className="w-full flex items-center gap-3 px-3 py-2.5">
                    <Icon className="w-5 h-5 text-coolslate/70 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <span className="block font-medium text-sm text-graphite truncate">
                            {file.source_filename}
                        </span>
                        {showFolder && folderLabel && (
                            <span className="block text-xs text-coolslate truncate">{folderLabel}</span>
                        )}
                    </div>
                    {linked && <LinkedBadge />}
                    <span className="text-xs text-coolslate shrink-0">
                        {formatDateTime(file.source_modified_at || file.updated_at, false)}
                    </span>
                </div>
            </button>
        );
    }

    // A split row, because the folder <Link> can't nest inside a row-wide <Link>.
    if (showFolder && folderLabel && folderHref) {
        return (
            <div className={`${cls} flex items-center gap-3 px-3 py-2.5`}>
                <Icon className="w-5 h-5 text-coolslate/70 shrink-0" />
                <div className="flex-1 min-w-0">
                    <Link to={fileHref} className="block hover:opacity-70 transition-opacity">
                        <span className="block font-medium text-sm text-graphite truncate">
                            {file.source_filename}
                        </span>
                    </Link>
                    <Link
                        to={folderHref}
                        title={folderLabel}
                        className="block text-xs text-coolslate truncate hover:text-brand-dark transition-colors"
                    >
                        {folderLabel}
                    </Link>
                </div>
                {linked && <LinkedBadge />}
                <span className="text-xs text-coolslate shrink-0">
                    {formatDateTime(file.source_modified_at || file.updated_at, false)}
                </span>
            </div>
        );
    }

    return (
        <Link to={fileHref} className={cls}>
            <div className="w-full flex items-center gap-3 px-3 py-2.5">
                <Icon className="w-5 h-5 text-coolslate/70 shrink-0" />
                <div className="flex-1 min-w-0">
                    <span className="block font-medium text-sm text-graphite truncate">
                        {file.source_filename}
                    </span>
                    {showFolder && folderLabel && (
                        <span className="block text-xs text-coolslate truncate">{folderLabel}</span>
                    )}
                </div>
                {linked && <LinkedBadge />}
                <span className="text-xs text-coolslate shrink-0">
                    {formatDateTime(file.source_modified_at || file.updated_at, false)}
                </span>
            </div>
        </Link>
    );
}
